<template>
    <div v-if="ready">
        <div class="flex flex-col border-solid border-2 text-center py-5 px-16">
            <h2>IPMI Console</h2>
            <p>IPMI (Intelligent Platform Management Interface) is a remote management interface that allows you to manage your server even if you lose network access or the system becomes unresponsive.</p>
            <p>With IPMI you can monitor hardware health, reboot your server or access a recovery console.</p>
            
            <div v-if="publicIpmiEnabled">
                <b-alert show variant="primary">
                    <span v-if="publicIpmiEndTime != 'Never'">Public IPMI access is currently enabled. It will be disabled automatically {{ publicIpmiEndTime }}. <br>Disabling public access can increase the security of your device.</span>
                    <span v-else>Public IPMI access is <strong>permanently enabled</strong>. <br>We strongly advise against leaving IPMI access enabled longer than necessary, as it may decrease the security of your server.</span>
                </b-alert>
            </div>
            <div>
                <b-button 
                    v-if="publicIpmiEnabled" 
                    variant="outline-primary" 
                    class="text-primary"
                    @click="updateIpmi()"
                    :disabled="submitting"
                    >
                    Disable public IPMI
                </b-button>
                <b-button v-b-modal.ipmi-modal variant="primary" class="text-white" :disabled="submitting">Enable Access</b-button>
            </div>
        </div>
        
        <b-modal 
            id="ipmi-modal" 
            title="Enable Public IPMI Access"
            centered
            @ok="updateIpmi"
            @cancel="selectedIpmiPublicAccessPeriod = null"
            :ok-disabled="selectedIpmiPublicAccessPeriod === null || (selectedIpmiPublicAccessPeriod.value == -1 && !orderIdConfirmed)"
            ok-variant="success"
            ok-title="Enable"
            cancel-variant="link"
        >
            
            <b-alert show variant="primary" class="my-5">
                Please note that enabling public IPMI access may decrease the security of your server. We strongly advise against leaving IPMI access enabled for longer than necessary.
            </b-alert>

            <label>Enable Public IPMI Access for:</label>
            <b-list-group>
                <b-list-group-item 
                    v-for="(period, index) in periods" 
                    :key="index" 
                    button 
                    :active="selectedIpmiPublicAccessPeriod === period"
                    @click="selectedIpmiPublicAccessPeriod = period">{{ period.text }}</b-list-group-item>
            </b-list-group>

            <template v-if="selectedIpmiPublicAccessPeriod && selectedIpmiPublicAccessPeriod.value == -1">
                <div class="row my-4">
                    <div class="col">
                        <p class="mb-1"><strong>Permanently enabling public IPMI access is not recommended.</strong></p>
                        <p>Please retype the Order ID ({{ orderId }}) for this server to confirm this change.</p>
                        <b-form-input v-model="confirmString" :state="orderIdConfirmed" placeholder="Order ID"></b-form-input>
                    </div>
                </div>
            </template>
        </b-modal>

        <div class="row pt-4" v-if="ready">
            <template v-if="hasData">
                <template v-if="remoteManagement.publicAccess">
                    <div class="col-sm-12 col-lg-6 drop">
                        <div class="sau-bx p-6 h-100">
                            <h5>IPMI Username</h5>
                            <p>{{ remoteManagement.directUser }}</p>
                        </div>
                    </div>
                    <div class="col-sm-12 col-lg-6">
                        <div class="sau-bx p-6 h-100 ">
                            <span class="mb-12">Password</span><br />
                            <div v-show="!showIpmiPassword">
                                <span class="sau-pwd-dot space" v-for="index in remoteManagement.directPassword.length" :key="index"></span>
                                <a href="#" class="sau-linx green sau-font sm pl-2" @click.prevent="showIpmiPassword = !showIpmiPassword">Show</a> / <b-link href="#" v-b-modal.modalChangeIpmiPassword class="sau-linx green sau-font sm">Change</b-link>
                            </div>
                            <div v-show="showIpmiPassword">
                                <span class="clear-password wm-hide">{{ remoteManagement.directPassword }}</span>
                                <a href="#" class="sau-linx green sau-font sm pl-2" @click.prevent="showIpmiPassword = !showIpmiPassword">Hide</a>
                            </div>
                        </div>
                    </div>
                </template>
            </template>

            <div class="col" v-else-if="pendingUpgrade">
                <div class="sau-bx bx-pad text-center">
                    <p class="sau-font sm">An upgrade request is currently pending for this service</p>
                </div>
            </div>

            <template v-else-if="!provisioned">
                <div class="row">
                    <div class="col">
                        <div class="sau-bx bx-pad">
                            <h3>Your Server is still being Provisioned</h3>
                            <p>Server Provisioning usually takes 5-45 minutes.  If it has taken longer than this, our support team will be investigating the issue.</p>
                            <p>If you would like assistance from one of our support staff please click the link below.</p>
                            <sau-utility-support></sau-utility-support>
                        </div>
                    </div>
                </div>
            </template>

            <div v-else class="col">

                <sau-apiro-response type="message">
                    <template slot="message">It looks like you don't have IPMI.</template>
                    <template slot="details">
                        <p>You'll need to upgrade to enable remote management to your server.</p>
                        <b-link v-b-modal.upgrade-service class="sau-link-button sm">Contact Sales Now!</b-link>
                        <sau-upgrade-modal v-on:updateFlag="updateUpgradeFlag"></sau-upgrade-modal>
                    </template>
                </sau-apiro-response>

            </div>
        </div>

        <template v-else>
            <sau-loading>
                <template slot="message">Fetching Remote Access Details</template>
            </sau-loading>
        </template>

        <div class="row pt-6" v-if="hasData && remoteManagement.publicAccess">
            <div class="col-lg-6">
                <div class="sau-bx p-6">
                    <h5>IPMI Console</h5>
                    <p>{{ remoteManagement.url }}</p>
                </div>
            </div>
        </div>
        <div class="row pt-12" v-if="hasData && remoteManagement.publicAccess">
            <div class="col">
                <a :href="remoteManagement.url" target="_blank" class="btn btn-success btn-sm p-4">Login to your IPMI Console</a>
            </div>
        </div>
        <!-- change Password -->
        <b-modal id="modalChangeIpmiPassword"
            title="Change your IPMI Password"
            @ok="changeIpmiPassword"
            @cancel="clearDetails"
            :ok-variant="btnColor"
            :ok-disabled="disableOk"
            ok-title="Change Password"
            centered
            cancel-variant="link">
            <form>
                <div class="row">
                    <div class="col-sm-12 col-lg-6 offset-lg-3">
                        <!-- New Password -->
                        <div class="form-group">
                            <label for="newIpmiPassword">Enter new IPMI Password</label>
                            <input
                                type="password"
                                class="form-control"
                                name="newIpmiPassword"
                                v-model="newIpmiPassword"
                                @input="$v.newIpmiPassword.$touch"
                                :class="{'is-invalid': $v.newIpmiPassword.$invalid && $v.newIpmiPassword.$dirty, 'is-valid': !$v.newIpmiPassword.$invalid && $v.newIpmiPassword.$dirty}"
                            />
                            <password-strength :password="newIpmiPassword"></password-strength>

                            <!-- New Password Validations -->
                            <!-- Validations -->
                            <div v-show="!$v.newIpmiPassword.$dirty">
                                <small class="text-muted">* Required</small>
                            </div><!-- default message -->
                            <div v-show="!$v.newIpmiPassword.$invalid">&nbsp;</div><!-- valid message -->
                            <div v-if="!$v.newIpmiPassword.required" class="invalid-feedback">* Required</div>
                        </div>
                        <!-- Re-enter Password -->
                        <div class="form-group">
                            <label for="reEnterIpmiPassword">Re-enter Password</label>
                            <input
                                type="password"
                                class="form-control"
                                name="reEnterIpmiPassword"
                                v-model="reEnterIpmiPassword"
                                @input="$v.reEnterIpmiPassword.$touch"
                                :class="{'is-invalid': $v.reEnterIpmiPassword.$invalid && $v.reEnterIpmiPassword.$dirty, 'is-valid': !$v.reEnterIpmiPassword.$invalid && $v.reEnterIpmiPassword.$dirty}"
                            />
                            <!-- Validations -->
                            <div v-show="!$v.reEnterIpmiPassword.$dirty">
                                <small class="text-muted">&nbsp;</small>
                            </div><!-- default message -->
                            <div v-show="!$v.reEnterIpmiPassword.$invalid">&nbsp;</div><!-- valid message -->
                            <div v-if="!$v.reEnterIpmiPassword.required" class="invalid-feedback">Passwords must match</div>
                        </div>
                    </div>
                </div>
            </form>
        </b-modal>
    </div>
</template>

<script>

import axios from 'axios';
import { required, sameAs } from 'vuelidate/lib/validators';
import UpgradeServiceModal from '@js/components/utilities/_UpgradeModal';
export default {
    components: {
        'sau-upgrade-modal' : UpgradeServiceModal
    },
    data() {
        return {
            ready: false,
            pendingUpgrade: false,
            provisioned: false,
            showIpmiPassword: false,
            ipmiPassword:'',
            btnColor:'success',
            error:{
                message:''
            },
            newIpmiPassword:'',
            reEnterIpmiPassword:'',
            id: '',
            publicIpmi: null,
            selectedIpmiPublicAccessPeriod: null,
            confirmString: '',
            periods: [
                {
                    text: '30 minutes',
                    value: 30,
                    warning: false,
                },
                {
                    text: '1 hour',
                    value: 60,
                    warning: false,
                },
                {
                    text: '2 hours',
                    value: 120,
                    warning: false,
                },
                {
                    text: '3 hours',
                    value: 180,
                    warning: false,
                },
                {
                    text: '6 hours',
                    value: 360,
                    warning: false,
                },
                {
                    text: 'Forever',
                    value: -1,
                    warning: true,
                },
            ],
            submitting: false,
        };
    },
    validations:{
        newIpmiPassword:{
            required
        },
        reEnterIpmiPassword:{
            sameAsPassword: sameAs('newIpmiPassword')
        }
    },
    created() {
        if(Object.keys(this.remoteManagement).length == 0) {
            this.startApp();
        } else {
            this.ready = true;
        }
    },
    computed: {
        disableOk() {
            if(!this.$v.$invalid) {
                this.btnColor = 'success'
                return false;
            } else {
                this.btnColor = 'light'
                return true;
            }
        },

        hasData() {
            return this.remoteManagement ? Object.keys(this.remoteManagement).length : false;
        },

        orderId() {
            return this.currentService.sau_order_id;
        },

        orderIdConfirmed() {
            return this.confirmString == this.orderId;
        },

        publicIpmiEndTime() {
            if (this.publicIpmi) {
                const minutesUntilDisable = _.get(this.publicIpmi, 'enabledMinutes', null);
                if(minutesUntilDisable === -1) {
                    return 'Never';
                } else if(minutesUntilDisable > 0) {
                    return moment().add(minutesUntilDisable, 'minutes').fromNow();
                }
            }
            return '';
        },

        publicIpmiEnabled() {
            if (this.publicIpmi) {
                return _.get(this.publicIpmi, 'enabled', false);
            }
            return false;
        },
    },
    methods:{
        startApp() {

            let accessPromise = this.getAccessDetails().then(response => {
                if(response.data.status == 'success') {
                    this.$store.commit('setRemoteManagement', response.data.device);
                    this.provisioned = true;
                } else if (response.data.status == 'error' && response.data.not_provisioned) {
                    this.provisioned = false;
                }
            }).catch(error => {
                // Provide catch so the promise still resolves on error
            });

            let upgradePromise = this.checkPendingUpgrade().then(response => {
                if(response.data.status == 'success') {
                    this.pendingUpgrade = response.data.pendingUpgrade;
                }
            }).catch(error => {
                // Provide catch so the promise still resolves on error
            });

            const publicIpmiPromise = axios.get(`/api/v2/services/${this.getServiceId()}/public-ipmi`)
                .then((response) => {
                    this.publicIpmi = response.data;
                }).catch((error) => {
                    console.log(error);
                })
        

            Promise.all([accessPromise, upgradePromise, publicIpmiPromise]).then(() => this.ready = true);

        },
        updateUpgradeFlag() {
            this.pendingUpgrade = true;
        },
        changeIpmiPassword() {
            let data = {
                directPassword: this.newIpmiPassword,
                directUser: this.remoteManagement.directUser
            }
            axios.post('/api/services/' + this.getServiceType() + '/' + this.getServiceId() + '/access/update', data)
            .then(response => {
                this.clearDetails();
                if(response.data.status == 'success') {
                    this.$store.commit('setDirectAccess', ['directPassword', data.directPassword]);
                    this.$swal({
                        type:'success',
                        title: 'Your IPMI password has been updated',
                        showConfirmButton: false,
                        timer: 3000
                    });
                }
            })
            .catch(error => {
                let message = '';
                _.forIn(error.response.data.errors, function(value, key) {
                    message += value+'<br />';
                });
                this.$swal({
                    type:'error',
                    title: 'There was an issue updating IPMI',
                    html: message,
                    showConfirmButton: false,
                });
            });
        },
        clearDetails() {
            this.newIpmiPassword = this.reEnterIpmiPassword  = '';
            this.$v.$reset();
        },

        updateIpmi() {
            this.submitting = true;
            
            const url = `/api/v2/services/${this.currentService.id}/public-ipmi`;
            const enabled = this.selectedIpmiPublicAccessPeriod != null;

            axios.post(url, {
                enabled: enabled,
                period: _.get(this.selectedIpmiPublicAccessPeriod, 'value', 0),
                confirmString: this.confirmString,
            })
                .then((response) => {
                    
                    this.$swal({
                        type:'success',
                        title: `Public IPMI access ${enabled ? 'enabled' : 'disabled'} succesfully`,
                        showConfirmButton: false,
                        timer: 2500
                    });
                    
                    this.startApp();
                })
                .catch((error) => {
                    this.$swal({
                        type:'error',
                        title: 'Failed to update public IPMI access. Please contact support if this problem persists.',
                        showConfirmButton: false,
                        timer: 2500
                    });
                })
                .finally(() => {
                    this.confirmString = '';
                    this.selectedIpmiPublicAccessPeriod = null;
                    this.submitting = false;
                });
        },
    }
}
</script>

<style>
.space {
    margin:0px 2px;
}
</style>
